import type { EstimateDetail, TemplateParamCatalogOutput, TemplateVersion, UpdateEstimateInput, UpdateInspectionInput } from 'used-vehicle-contracts';
import { StatCard } from '../../components/ui/DefinitionList';
import { formatMoney } from '../../lib/format';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { CostItemsCard } from '../costs/CostItemsCard';
import { InspectionFields } from '../inspections/InspectionFields';
import type { EstimatorForm } from './estimator-form';
import type { LocalQuickRange, LocalValuation } from './valuation-local';

type FactsPatch = Omit<UpdateEstimateInput, 'estimateId' | 'expectedRevision'>;
type InspectionPatch = Omit<UpdateInspectionInput, 'owner'>;

export interface EstimatorStep2Props {
  detail: EstimateDetail;
  form: EstimatorForm;
  local: { valuation: LocalValuation; quickRange: LocalQuickRange } | null;
  templateValues: TemplateVersion | null;
  damageOptions: TemplateParamCatalogOutput | null;
  onChange: (patch: Partial<EstimatorForm>, save: FactsPatch | null, inspectionSave: InspectionPatch | null) => void;
  /** A cost item moves the ceiling, so the page re-reads the estimate after every one (plan §5.5). */
  onCostsChanged: () => void;
}

/**
 * Step 2 — the manual inspection and the expected work (plan §7).
 *
 * The fields themselves are `InspectionFields`, shared with the stock vehicle's Inspection tab: the
 * purchase copies the row from one owner to the other, so writing the form twice would mean two places
 * where "unknown" could accidentally become "excellent" (PP AC03).
 *
 * The "Expected work" card is the one addition beyond the prototype (plan §11.4): repairs a salesperson
 * already knows about lower the ceiling *before* the offer is made, and the purchase carries them onto
 * the vehicle as the workshop's job list.
 */
export const EstimatorStep2 = ({ detail, form, local, templateValues, damageOptions, onChange, onCostsChanged }: EstimatorStep2Props) => {
  const { t } = useI18nTranslation();

  const missing = local?.valuation.built.missing ?? [];
  const blocking = local?.valuation.built.blocking ?? [];
  const appraisal = local?.valuation.run.outputs.appraisal ?? null;
  const isComplete = missing.length === 0 && blocking.length === 0 && (local?.valuation.ok ?? false);
  const isEditable = detail.status !== 'DEAL_AGREED' && detail.status !== 'NO_DEAL';

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
      <div className="space-y-4">
        <InspectionFields
          values={{
            grade7: form.grade7,
            interior: form.interior,
            engine: form.engine,
            hybrid: form.hybrid,
            tires: form.tires,
            damageGroup1Index: form.damageGroup1Index,
            damageGroup2Index: form.damageGroup2Index,
            inspectorNotes: form.inspectorNotes,
          }}
          fuel={form.fuel}
          damageOptions={damageOptions}
          templateValues={templateValues}
          disabled={!isEditable}
          onChange={(values, patch) => onChange(values as Partial<EstimatorForm>, null, patch as InspectionPatch)}
        />

        <CostItemsCard
          owner={{ type: 'ESTIMATE', id: detail.id }}
          editable={isEditable}
          title={t('estimates.expectedWorkTitle')}
          description={t('estimates.expectedWorkBody')}
          onChanged={onCostsChanged}
        />
      </div>

      <aside className="h-fit space-y-3 lg:sticky lg:top-6">
        <StatCard
          label={t('estimates.currentAppraisal')}
          value={
            isComplete && appraisal !== null ? (
              <span
                dir="ltr"
                className="tabular-nums"
              >
                {formatMoney(appraisal)}
              </span>
            ) : (
              t('estimates.incompleteCount', { count: missing.length })
            )
          }
          hint={isComplete ? t('estimates.conditionComplete') : t('estimates.conditionIncomplete')}
        />

        {missing.length > 0 ? (
          <div className="rounded-lg border border-border bg-card p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('estimates.missingTitle')}</p>
            <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
              {missing.map((entry) => (
                <li key={entry.name}>{t(`estimates.fields.${entry.field}`, { defaultValue: entry.field })}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {blocking.length > 0 ? (
          <div
            role="alert"
            className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive"
          >
            <p className="font-medium">{t('estimates.blockingTitle')}</p>
            <ul className="mt-2 space-y-1">
              {blocking.map((entry) => (
                <li key={`${entry.code}-${entry.field}`}>{entry.detail}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <p className="rounded-md border border-border bg-muted px-3 py-2 text-xs text-muted-foreground">{t('estimates.documentsLater')}</p>

        {detail.inspection?.inspectedAt ? <p className="text-xs text-muted-foreground">{t('estimates.inspectionRecorded')}</p> : null}
      </aside>
    </div>
  );
};
